import { REFUSED_NO_EMAIL, REFUSED_NOT_ALLOWED } from "@phone-booth/content/allowlist";

/**
 * Turning what Cognito sends back into something worth reading.
 *
 * When the pre-sign-up trigger refuses an account, Cognito does not pass the
 * trigger's sentence through. It wraps it — "PreSignUp failed with error <the
 * message>." — and puts that in `error_description`. Shown as it arrives, the
 * screen names an internal Lambda trigger at somebody who only wanted to know
 * whether they can sign in.
 *
 * So the refusals the booth raises itself are recognised and rewritten. Anything
 * else is tidied and shown as it came, because an error nobody predicted is
 * more useful in the reporter's own words than flattened into "sign-in failed".
 */

/** What the sign-in screen shows: a bold line, and an optional line under it. */
export interface SignInFailure {
  title: string;
  detail?: string;
}

/** Thrown by the sign-in callback so the screen gets both lines, not a string. */
export class SignInError extends Error {
  readonly failure: SignInFailure;

  constructor(failure: SignInFailure) {
    super(failure.title);
    this.failure = failure;
  }
}

/**
 * Cognito's wrapper, and the full stop it adds to a message that may already
 * have one. Both are stripped before anything unrecognised is shown.
 */
function tidy(description: string): string {
  return description
    .replace(/^PreSignUp failed with error\s*/i, "")
    .replace(/^PostConfirmation failed with error\s*/i, "")
    .replace(/\.\.+$/, ".")
    .trim();
}

export function signInFailure(
  description: string | null,
  code: string | null,
): SignInFailure {
  const text = description ? tidy(description) : "";

  if (text.includes(REFUSED_NOT_ALLOWED)) {
    return {
      title: "That account cannot sign in here",
      // The line under the card already says only the list gets in, so this
      // says the part that line does not: what to do about it.
      detail:
        "Ask someone who can already sign in to add you to the list, then try again.",
    };
  }
  if (text.includes(REFUSED_NO_EMAIL)) {
    return {
      title: "Google did not share an email address",
      detail:
        "The booth recognises people by their email address. Try again and allow Google to share it.",
    };
  }
  // `access_denied` is what Google sends when somebody closes the consent
  // screen, which is a decision rather than a failure.
  if (code === "access_denied") {
    return { title: "Sign-in was cancelled", detail: "Nothing has changed." };
  }
  return {
    title: "Sign-in failed",
    ...(text ? { detail: text } : {}),
  };
}
